/**
 * Adaptive polling system that optimizes performance based on context
 */

import { UI_CONSTANTS } from './submissionActivityConstants';

export class AdaptivePoller {
	constructor(pollFunction, options = {}) {
		this.pollFunction = pollFunction;
		this.isRunning = false;
		this.pollInterval = null;
		this.retryCount = 0;
		
		// Configuration with smart defaults
		this.config = {
			baseInterval: options.baseInterval || UI_CONSTANTS.pollingInterval,
			maxInterval: options.maxInterval || 5 * 60 * 1000, // 5 minutes max
			minInterval: options.minInterval || 10 * 1000, // 10 seconds min
			backoffMultiplier: options.backoffMultiplier || 1.5,
			maxRetries: options.maxRetries || 3,
			enableVisibilityOptimization: options.enableVisibilityOptimization !== false,
			enableAdaptiveInterval: options.enableAdaptiveInterval !== false,
			...options
		};
		
		this.currentInterval = this.config.baseInterval;
		this.lastSuccessTime = null;
		this.errorCount = 0;
		
		// Page visibility tracking
		this.isPageVisible = !document.hidden;
		this.setupVisibilityTracking();
	}
	
	/**
	 * Sets up page visibility change detection
	 */
	setupVisibilityTracking() {
		if (!this.config.enableVisibilityOptimization) return;
		
		const handleVisibilityChange = () => {
			const wasVisible = this.isPageVisible;
			this.isPageVisible = !document.hidden;
			
			if (process.env.NODE_ENV === 'development') {
				console.log(`📱 Page visibility: ${this.isPageVisible ? 'visible' : 'hidden'}`);
			}
			
			// If page becomes visible after being hidden, do immediate poll
			if (!wasVisible && this.isPageVisible && this.isRunning) {
				this.doPoll();
			}
			
			// Adjust polling based on visibility
			this.adjustPolling();
		};
		
		document.addEventListener('visibilitychange', handleVisibilityChange);
		
		// Store reference for cleanup
		this.visibilityHandler = handleVisibilityChange;
	}
	
	/**
	 * Adjusts polling interval based on current conditions
	 */
	adjustPolling() {
		if (!this.config.enableAdaptiveInterval) return;
		
		let newInterval = this.config.baseInterval;
		
		// Slow down polling when page is not visible
		if (!this.isPageVisible) {
			newInterval = Math.min(this.config.maxInterval, this.config.baseInterval * 3);
		}
		
		// Increase interval on consecutive errors (exponential backoff)
		if (this.errorCount > 0) {
			newInterval = Math.min(
				this.config.maxInterval,
				this.config.baseInterval * Math.pow(this.config.backoffMultiplier, this.errorCount)
			);
		}
		
		// Ensure minimum interval
		newInterval = Math.max(this.config.minInterval, newInterval);
		
		if (newInterval !== this.currentInterval) {
			this.currentInterval = newInterval;
			if (this.isRunning) {
				this.restart();
			}
			
			if (process.env.NODE_ENV === 'development') {
				console.log(`⏱️ Polling interval adjusted to ${newInterval / 1000}s`);
			}
		}
	}
	
	/**
	 * Executes the poll function with error handling
	 */
	async doPoll() {
		try {
			await this.pollFunction();
			this.lastSuccessTime = Date.now();
			this.errorCount = 0; // Reset error count on success
			this.adjustPolling(); // Readjust after successful poll
		} catch (error) {
			this.errorCount++;
			
			if (process.env.NODE_ENV === 'development') {
				console.warn(`⚠️ Polling error (${this.errorCount}/${this.config.maxRetries}):`, error.message);
			}
			
			// Stop polling if too many consecutive errors
			if (this.errorCount >= this.config.maxRetries) {
				if (process.env.NODE_ENV === 'development') {
					console.error('🛑 Stopping polling due to consecutive errors');
				}
				this.stop();
				return;
			}
			
			this.adjustPolling(); // Increase interval on error
		}
	}
	
	/**
	 * Starts the polling system
	 */
	start() {
		if (this.isRunning) return;
		
		this.isRunning = true;
		this.errorCount = 0;
		this.currentInterval = this.config.baseInterval;
		
		// Do initial poll after short delay
		setTimeout(() => {
			if (this.isRunning) {
				this.doPoll();
			}
		}, UI_CONSTANTS.initialPollingDelay);
		
		// Set up regular polling
		this.pollInterval = setInterval(() => {
			if (this.isPageVisible || !this.config.enableVisibilityOptimization) {
				this.doPoll();
			}
		}, this.currentInterval);
		
		if (process.env.NODE_ENV === 'development') {
			console.log(`🚀 Adaptive polling started (${this.currentInterval / 1000}s interval)`);
		}
	}
	
	/**
	 * Stops the polling system
	 */
	stop() {
		if (!this.isRunning) return;
		
		this.isRunning = false;
		
		if (this.pollInterval) {
			clearInterval(this.pollInterval);
			this.pollInterval = null;
		}
		
		if (process.env.NODE_ENV === 'development') {
			console.log('🛑 Adaptive polling stopped');
		}
	}
	
	/**
	 * Restarts polling with current settings
	 */
	restart() {
		if (!this.isRunning) return;
		
		// Clear existing interval without stopping completely
		if (this.pollInterval) {
			clearInterval(this.pollInterval);
		}
		
		// Set up new interval with updated timing
		this.pollInterval = setInterval(() => {
			if (this.isPageVisible || !this.config.enableVisibilityOptimization) {
				this.doPoll();
			}
		}, this.currentInterval);
		
		if (process.env.NODE_ENV === 'development') {
			console.log(`🔄 Polling interval updated to ${this.currentInterval / 1000}s`);
		}
	}
	
	/**
	 * Triggers an immediate poll (useful for manual refresh)
	 */
	pollNow() {
		if (this.isRunning) {
			this.doPoll();
		}
	}
	
	/**
	 * Gets current polling statistics
	 */
	getStats() {
		return {
			isRunning: this.isRunning,
			currentInterval: this.currentInterval,
			errorCount: this.errorCount,
			lastSuccessTime: this.lastSuccessTime,
			isPageVisible: this.isPageVisible,
			timeSinceLastSuccess: this.lastSuccessTime ? Date.now() - this.lastSuccessTime : null
		};
	}
	
	/**
	 * Cleanup method
	 */
	destroy() {
		this.stop();
		
		if (this.visibilityHandler) {
			document.removeEventListener('visibilitychange', this.visibilityHandler);
		}
	}
}